import React, { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";

import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Footer from "../../../components/footer/Footer";
import Scrollbar from "../../../components/scrollbar/scrollbar";

import "./FPGAEngineering.css";
import "./PageTheme.css";
import serviceImage from "../../../images/engineering-capability-hero.png";

type Block = { title?: string; text?: string; lines?: string[]; raw: string };

const config = {
  "title": "FPGA Engineering",
  "category": "Advanced Design & Implementation",
  "visual": "fpga",
  "folder": "FPGAEngineering",
  "sections": [
    [
      "Accelerating Hardware Innovation Through FPGA Engineering\nFPGA technology enables engineering teams to rapidly prototype, validate, and optimize complex hardware designs before they progress to production silicon. Its flexibility makes it an essential platform for functional verification, hardware acceleration, algorithm evaluation, embedded system development, and early software integration—helping organizations reduce development risk while accelerating time-to-market.\n\nDigicomm Semiconductor delivers specialized FPGA Engineering capabilities that support the complete FPGA development lifecycle. From architecture definition and RTL implementation to IP integration, prototyping, validation, and performance optimization, our engineering teams work collaboratively with customers to develop reliable, scalable, and implementation-ready FPGA solutions through disciplined engineering execution.\n\nPrimary CTA\nDiscuss Your FPGA Engineering Requirements"
    ],
    [
      "Capability Overview\nFPGA Engineering provides a flexible and efficient platform for developing, validating, and optimizing hardware designs before they transition to production silicon. Its programmable architecture enables engineering teams to evaluate design concepts, accelerate functional validation, support early software development, and reduce implementation risk while shortening overall product development cycles.\n\nDigicomm Semiconductor delivers specialized FPGA Engineering capabilities that support every stage of FPGA development. From RTL implementation and IP integration to FPGA prototyping, hardware validation, performance optimization, and board-level integration, our engineering teams collaborate closely with customer organizations to develop reliable, scalable, and application-ready FPGA solutions that accelerate engineering execution and strengthen product confidence."
    ],
    [
      "Why It Matters\nAs semiconductor products continue to grow in complexity, engineering teams face increasing pressure to validate functionality, accelerate development, and reduce implementation risk without compromising product quality. FPGA Engineering provides a flexible platform for rapidly prototyping hardware, evaluating system performance, enabling early software development, and validating design concepts before committing to production silicon.\n\nBy supporting iterative development and real-world hardware evaluation, FPGA Engineering helps organizations identify design challenges early, improve engineering confidence, shorten development cycles, and optimize product readiness. It serves as a critical bridge between design intent and production implementation, enabling faster innovation and more predictable development outcomes.\n\nKey Outcomes :\nAccelerates Hardware Development\nEnable rapid prototyping and iterative hardware development to shorten engineering cycles and speed product realization.\n\nStrengthens Functional Validation\nEvaluate design functionality on hardware early in the development process, improving confidence before production implementation.\n\nReduces Development Risk\nIdentify design issues sooner through hardware-based validation, minimizing costly downstream changes and engineering rework.\n\nEnables Early Software Development\nSupport firmware and software development alongside hardware, improving system integration and overall project efficiency.\n\nImproves Design Flexibility\nAdapt, refine, and optimize hardware designs quickly through programmable architectures that support evolving product requirements.\n\nSupports Confident Product Realization\nBuild engineering confidence through disciplined FPGA development, validation, and optimization that contribute to successful semiconductor products."
    ],
    [
      "FPGA Engineering Scope\nDevelop scalable FPGA architectures aligned with functional requirements, performance objectives, and application-specific design goals.\n\nRTL Implementation\nImplement and optimize RTL designs for FPGA platforms, ensuring functional accuracy, efficient resource utilization, and reliable operation.\n\nIP Integration\nIntegrate standard and custom IP cores into FPGA designs while ensuring compatibility, performance, and seamless subsystem interaction.\n\nFPGA Prototyping\nBuild FPGA-based prototypes that enable rapid hardware validation, design evaluation, and early functional verification before ASIC implementation.\n\nBoard Bring-up & Hardware Validation\nSupport board-level integration, hardware bring-up, debugging, and validation to establish stable and reliable system operation.\n\nPerformance Optimization\nOptimize FPGA implementations for timing, resource utilization, power efficiency, and overall application performance.\n\nInterface & Connectivity Implementation\nDevelop and integrate high-speed communication interfaces and peripheral connectivity to support complete FPGA-based systems.\n\nSystem Integration & Verification Support\nCollaborate with hardware, software, and verification teams to ensure successful FPGA integration within broader semiconductor and embedded system development programs."
    ],
    [
      "Key Benefits\nFPGA Engineering enables organizations to innovate faster by providing a flexible platform for hardware development, functional validation, and system optimization. Digicomm's FPGA Engineering capabilities help accelerate engineering execution, improve product confidence, and reduce development risk through disciplined implementation and collaborative engineering practices.\n\nAccelerated Product Development\nReduce hardware development cycles through rapid FPGA implementation, prototyping, and iterative engineering refinement.\n\nImproved Functional Validation\nValidate hardware functionality early in the development lifecycle, strengthening engineering confidence before production implementation.\n\nGreater Engineering Flexibility\nAdapt and optimize designs efficiently through programmable hardware platforms that support evolving product requirements.\n\nReduced Development Risk\nIdentify and resolve design challenges earlier through FPGA-based validation, minimizing costly downstream engineering changes.\n\nOptimized System Performance\nEnhance hardware performance, resource utilization, and overall implementation quality through structured FPGA optimization techniques.\n\nConfident Product Realization\nSupport successful product development through reliable FPGA engineering, disciplined execution, and collaborative technical delivery."
    ],
    [
      "Technology Expertise\nAchieving reliable timing closure requires more than comprehensive timing analysis—it demands expertise across advanced sign-off methodologies, process variation modeling, optimization strategies, and implementation-aware engineering practices. Digicomm's Static Timing Analysis teams work across modern STA methodologies and industry-standard implementation environments to support robust timing convergence and predictable silicon performance.\n\nSTA Methodologies\nStatic Timing Analysis (STA)\nMulti-Mode Multi-Corner (MMMC) Analysis\nTiming Constraint Development\nTiming Sign-off Methodologies\nTiming Closure Strategies\n\nTiming Analysis & Sign-off\nSetup & Hold Analysis\nRecovery & Removal Checks\nClock Domain Analysis\nClock Skew & Latency Analysis\nTiming Report Analysis\nSign-off Validation\n\nOptimization & Variation Analysis\nOn-Chip Variation (OCV)\nAdvanced On-Chip Variation (AOCV)\nParametric On-Chip Variation (POCV)\nSignal Integrity (SI) Aware Timing\nCrosstalk Analysis Support\nTiming Optimization\n\nImplementation Integration\nPhysical Design Collaboration\nClock Tree Synthesis (CTS) Support\nECO Timing Validation\nTiming Closure Support\nImplementation Readiness\n\nEDA Environment\n(Include only platforms actively used by Digicomm)\nSynopsys PrimeTime\nCadence Tempus\nSiemens EDA"
    ],
    [
      "Why Digicomm\nFPGA Engineering plays a vital role in accelerating hardware innovation, validating complex designs, and reducing product development risk. Delivering successful FPGA solutions requires more than implementation expertise—it demands structured engineering, practical problem-solving, and seamless collaboration across multidisciplinary teams. Digicomm combines specialized FPGA capabilities with disciplined execution to help customers develop high-performance solutions with greater speed, flexibility, and confidence.\n\nWhat Sets Us Apart:\nSpecialized FPGA Expertise\nDedicated FPGA engineering capabilities spanning architecture development, RTL implementation, prototyping, hardware validation, and system integration across diverse application domains.\n\nEngineering Agility\nStructured engineering methodologies that support rapid prototyping, iterative development, and efficient design refinement while maintaining implementation quality.\n\nCollaborative Development\nFPGA engineers who work seamlessly with hardware, software, verification, and system engineering teams to accelerate development and strengthen technical alignment.\n\nScalable Engineering Support\nFlexible engagement models that adapt to evolving project requirements, FPGA platforms, application complexity, and development timelines.\n\nCommitment to Reliable Delivery\nA disciplined engineering approach focused on implementation quality, hardware validation, and dependable FPGA solutions that support successful product realization."
    ],
    [
      "Related Specialized Engineering Capabilities\nEmbedded Software Systems\nEmbedded firmware and software development that enables seamless hardware-software integration across FPGA and embedded platforms.\nLearn More →\n\nHardware Platform Engineering\nEngineering support for hardware platforms, development boards, system integration, and validation environments that complement FPGA-based development.\nLearn More →\n\nRelated Core Engineering Services\nASIC & SoC Design\nDevelop scalable digital architectures that can be rapidly prototyped, evaluated, and refined through FPGA implementation.\nLearn More →\n\nVerification Services\nStrengthen functional validation through complementary verification methodologies that support robust FPGA development and hardware confidence.\n\nLearn More →\n\nRelated Engineering Engagement Models\nEngineering Consulting\n\nAccess specialized FPGA expertise to accelerate hardware development, solve implementation challenges, and optimize system performance.\n\nLearn More →\n\nDedicated Engineering Teams\n\nExtend your engineering organization with experienced FPGA specialists who integrate seamlessly into ongoing hardware development programs.\n\nLearn More →\n\nWhy I recommend this structure\n\nThe relationships are based on how FPGA engineering is typically used in real semiconductor programs.\n\nRelated Specialized Engineering Capabilities\n\nFPGA Engineering naturally works alongside:\n\nEmbedded Software Systems — because firmware and software are often developed in parallel with FPGA hardware.\nHardware Platform Engineering — because FPGA designs ultimately need hardware platforms for bring-up, integration, and validation.\nRelated Core Engineering Services\n\nFPGA projects frequently connect with:\n\nASIC & SoC Design — FPGA serves as an ideal platform for architectural evaluation and prototyping before ASIC implementation.\nVerification Services — FPGA complements simulation and verification by enabling hardware-based functional validation.\nRelated Engineering Engagement Models\n\nRather than ending with technical capabilities alone, we also provide a pathway for visitors to explore how they can engage Digicomm's FPGA expertise, maintaining consistency across the website."
    ],
    [
      "Let's Accelerate Hardware Innovation Together\n\nFrom rapid prototyping and hardware validation to system integration and performance optimization, successful FPGA Engineering requires specialized expertise, disciplined execution, and close technical collaboration.\n\nWhether you're developing next-generation hardware, validating complex designs, or expanding your engineering capabilities with FPGA expertise, Digicomm Semiconductor is ready to support your team with focused engineering competency, collaborative execution, and dependable delivery.\n\nPrimary CTA\nDiscuss Your FPGA Engineering Requirements"
    ]
  ]
} as const;

const splitBlocks = (raw: string): string[] =>
  raw.split(/\n\s*\n/g).map((x) => x.trim()).filter(Boolean);

const asBlock = (raw: string): Block => {
  const lines = raw.split("\n").map((x) => x.trim()).filter(Boolean);
  if (lines.length >= 2 && lines[0].length <= 74) {
    const rest = lines.slice(1);
    const listLike = rest.length >= 3 && rest.every((x) => x.length < 78);
    if (listLike) return { title: lines[0], lines: rest, raw };
    return { title: lines[0], text: rest.join(" "), raw };
  }
  if (lines.length === 1) return { text: lines[0], raw };
  return { text: lines.join(" "), raw };
};

const getHero = (section: readonly string[], pageTitle: string) => {
  const merged = section.join("\n\n");
  const blocks = splitBlocks(merged);
  const filtered = blocks.filter((x) => x !== "Primary CTA");
  if (filtered[0] === pageTitle) filtered.shift();

  const first = filtered.shift() || pageTitle;
  const firstLines = first.split("\n").map((x) => x.trim()).filter(Boolean);
  const headline = firstLines[0] || pageTitle;
  const body: string[] = [];

  if (firstLines.length > 1) body.push(firstLines.slice(1).join(" "));
  filtered.forEach((b) => body.push(b));

  let cta = "";
  if (body.length && body[body.length - 1].length < 85) cta = body.pop() || "";
  return { headline, body, cta };
};

const Arrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h13M13 7l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EngineeringVisual: React.FC = () => (
  <div className="ses-visual service-engineering-image">
    <img src={serviceImage} alt="Configurable semiconductor chip with interconnected engineering circuitry" />
  </div>
);

const ContentPanel: React.FC<{ raw: string; index: number }> = ({ raw, index }) => {
  const blocks = splitBlocks(raw).filter((x) => x !== "Primary CTA");
  return (
    <div className="ses-block-grid">
      {blocks.map((rawBlock, i) => {
        const block = asBlock(rawBlock);
        const long = rawBlock.length > 430;
        const labelOnly =
          !block.title &&
          !!block.text &&
          block.text.length < 80 &&
          (/[:：]$/.test(block.text) || i === 0);

        return (
          <article
            className={`ses-block ${long ? "ses-block--wide" : ""} ${labelOnly ? "ses-block--label" : ""}`}
            key={`${index}-${i}`}
          >
            {block.title && <h3>{block.title}</h3>}
            {block.text && (labelOnly ? <h3>{block.text}</h3> : <p>{block.text}</p>)}
            {block.lines && <ul>{block.lines.map((line) => <li key={line}>{line}</li>)}</ul>}
          </article>
        );
      })}
    </div>
  );
};

const RelatedPanel: React.FC<{ raw: string }> = ({ raw }) => {
  const blocks = splitBlocks(raw);
  const routeMap: Record<string, string> = {
    "Analog & Mixed Signal Design & Layout": "/services/analog-mixed-signal",
    "Analog & Mixed Signal": "/services/analog-mixed-signal",
    "Static Timing Analysis (STA)": "/services/static-timing-analysis",
    "FPGA Engineering": "/services/fpga-engineering",
    "Embedded Software Systems": "/services/embedded-software-systems",
    "Hardware Platform Engineering": "/services/hardware-platform-engineering",
    "Networking Solutions": "/services/networking-solutions",
    "Silicon Validation": "/services/silicon-validation",
    "EDA & Design Automation": "/services/eda-design-automation",
    "Product Engineering": "/services/product-engineering",
    "ASIC & SoC Design": "/services/asic-soc-design",
    "Physical Design": "/services/physical-design",
    "Verification Services": "/services/verification",
  };

  const cards: React.ReactNode[] = [];
  let group = "";

  blocks.forEach((b, i) => {
    if (/^Related .*:$/.test(b.trim())) {
      group = b.trim();
      return;
    }
    if (b === "Learn More →") return;

    const lines = b.split("\n").map((x) => x.trim()).filter(Boolean);
    const title = lines[0];
    const desc = lines.slice(1).join(" ");

    if (routeMap[title]) {
      cards.push(
        <Link className="ses-related-card" to={routeMap[title]} key={`${title}-${i}`}>
          <span>{group}</span>
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
          <em>Learn More <Arrow/></em>
        </Link>
      );
    } else {
      cards.push(
        <article className="ses-related-card" key={`${title}-${i}`}>
          <span>{group}</span>
          <h3>{title}</h3>
          {desc && <p>{desc}</p>}
        </article>
      );
    }
  });

  return <div className="ses-related-grid">{cards}</div>;
};

const Page: React.FC = () => {
  const sections = config.sections as readonly (readonly string[])[];
  const hero = getHero(sections[0] || [], config.title);

  const lastSection = sections[sections.length - 1] || [];
  const lastRaw = lastSection.join("\n\n");
  const lastBlocks = splitBlocks(lastRaw).filter((x) => x !== "Primary CTA");
  const ctaLabel =
    lastBlocks.length && lastBlocks[lastBlocks.length - 1].length < 90
      ? lastBlocks[lastBlocks.length - 1]
      : "";
  const ctaContent = ctaLabel ? lastBlocks.slice(0, -1) : lastBlocks;
  const hasRelated = sections.length === 9;

  useEffect(() => {
    document.title = `${config.title} | Digicomm Semiconductor`;
  }, []);

  return (
    <Fragment>
      <div className={`specialized-service-page inner-page fpga-redesign`}>
        <div className="body_wrap o-clip">
          <Header />

          <main>
            <PageTitle pageTitle={config.title} pagesub={config.title} />

            <section className="ses-hero">
              <div className="ses-shell ses-two-col">
                <div className="ses-hero__copy">
                  <h1>{hero.headline}</h1>
                  {hero.body.map((p, i) => <p key={i}>{p}</p>)}
                  {hero.cta && (
                    <Link className="ses-btn" to="/contact">
                      {hero.cta}<Arrow/>
                    </Link>
                  )}
                </div>
                <EngineeringVisual />
              </div>
            </section>

            {sections.slice(1, hasRelated ? -2 : -1).map((section, idx) => {
              const raw = section.join("\n\n");
              const blocks = splitBlocks(raw);
              const lead = blocks[0] || "";
              const firstLine = lead.split("\n")[0] || "";

              return (
                <section className={`ses-section ses-section--${idx + 2} ${idx % 2 ? "ses-section--alt" : ""}`} key={idx}>
                  <div className="ses-shell">
                    <div className="ses-section-head">
                      <span>{String(idx + 2).padStart(2, "0")}</span>
                      <h2>{firstLine}</h2>
                    </div>
                    <ContentPanel raw={raw} index={idx}/>
                  </div>
                </section>
              );
            })}

            {hasRelated && (
              <section className="ses-section ses-related">
                <div className="ses-shell">
                  <RelatedPanel raw={sections[sections.length - 2].join("\n\n")}/>
                </div>
              </section>
            )}

            <section className="ses-cta">
              <div className="ses-shell ses-two-col">
                <div className="ses-cta__copy">
                  {ctaContent.map((b, i) => {
                    const lines = b.split("\n").map((x) => x.trim()).filter(Boolean);
                    return i === 0
                      ? <h2 key={i}>{lines.join(" ")}</h2>
                      : <p key={i}>{lines.join(" ")}</p>;
                  })}

                  {ctaLabel && (
                    <Link className="ses-btn" to="/contact">
                      {ctaLabel}<Arrow/>
                    </Link>
                  )}
                </div>
                <EngineeringVisual />
              </div>
            </section>
          </main>

          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default Page;
